import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import type { ValidLibraryObject } from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { PurchaseDialogBody } from './PurchaseDialogBody'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { toast } from 'react-hot-toast'
import { useEffect, useState } from 'react'
import { Dialog } from 'components/dialogs/Dialog'
import { createRandomId } from 'util/randomId'
import { Spinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import type { Section } from '@breakoutlearning/firebase-repository/models/Section'

const SlideDeckDetailsWidget = function ({
  libraryObject,
}: {
  libraryObject: ValidLibraryObject
}) {
  const { tt } = useTranslationTyped()
  return (
    <div className="bg-lightGray flex h-[250px] w-[250px] flex-col items-center justify-between rounded-2xl p-[20px] shadow-2xl">
      <div className="mb-4 flex flex-grow items-end justify-center rounded-lg">
        <img
          src={libraryObject.slideDeckImageURL || ''}
          alt=""
          className="max-h-[50px]"
        />
      </div>
      <div>
        {libraryObject.slideDeckTeaser && (
          <p className="mb-1 text-center text-body-medium text-gray-500">
            {libraryObject.slideDeckName}
          </p>
        )}
        <p className="line-clamp-2 text-center text-title-large">
          {libraryObject.slideDeckTeaser || libraryObject.slideDeckName}
        </p>
      </div>
      <div className="mt-4 rounded-full bg-core-primary px-4 py-1 text-center text-label-medium text-core-on-primary">
        {libraryObject.slideDeckPrice}{' '}
        {libraryObject.slideDeckPrice > 1
          ? tt.student_assignment.tokens()
          : tt.student_assignment.token()}
      </div>
    </div>
  )
}

export const EnrollDialogBody = observer(function EnrollDialogBody({
  cubit,
  headingId,
}: {
  cubit: StudentAssignmentCubit
  headingId: string
}) {
  const repo = useRepository()
  const { availableTokens } = useBreakoutUser()
  const tokensRequired = cubit.slideDeck.data.slideDeckPrice
  //const totalPrice = (Math.max(((tokensRequired - tokensAvailable) * 10), 0) / 100).toFixed(2);
  const { showDialog, popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const [enrollInProgress, setEnrollInProgress] = useState(false)

  useEffect(() => {
    repo.logEvent('enroll_dialog_opened')
  }, [repo])

  if (enrollInProgress) {
    return (
      <div className="flex flex-grow items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <DialogCloseButton />
      <h1 id={headingId} className="text-center text-headline-large">
        {tt.student_assignment.enroll_in_case()}
      </h1>
      <p className="text-center text-body-medium">
        {getEnrollOutcome(tokensRequired, availableTokens, tt)}
      </p>
      <SlideDeckDetailsWidget libraryObject={cubit.libraryObject} />
      <div className="text-body-medium">
        <div className="flex-row justify-between">
          <span>{tt.student_assignment.tokens_available_colon()}</span>
          <span> </span>
          <strong
            className={classNames(
              'text-title-small',
              tokensRequired > availableTokens ? 'text-core-error' : ''
            )}
          >
            {availableTokens}{' '}
            {availableTokens === 1
              ? tt.student_assignment.token()
              : tt.student_assignment.tokens()}
          </strong>
        </div>
      </div>
      <EnrollOrPurchaseButton
        onEnroll={async () => {
          try {
            repo.logEvent('enroll_button_clicked')
            setEnrollInProgress(true)
            const { success, message } = await cubit.enroll()
            setEnrollInProgress(false)
            repo.logEvent('enroll_completed', { success })
            if (success) return popDialog()
            if (message) toast.error(message)
          } catch (e) {
            repo.logEvent('enroll_button_failed')
            toast.error(tt.student_assignment.enroll_failure())
            popDialog()
            // rethrow to bubble to global error handler (sentry)
            throw e
          }
        }}
        onPurchase={() => {
          //close this dialog and show purchase dialog
          popDialog()
          showDialog(() => {
            const purchaseHeadingId = createRandomId()
            return (
              <Dialog
                size="md"
                innerClassName="flex flex-col items-center justify-between gap-2"
                ignoreBackdropClick
                aria-labelledby={purchaseHeadingId}
              >
                <PurchaseDialogBody
                  cubit={cubit}
                  headingId={purchaseHeadingId}
                />
              </Dialog>
            )
          })
        }}
        slideDeckPrice={cubit.slideDeck.data.slideDeckPrice}
        requiresPurchase={tokensRequired > availableTokens}
        section={cubit.section}
      />
    </>
  )
})

function getEnrollOutcome(
  tokensRequired: number,
  tokensAvailable: number,
  tt: ReturnType<typeof useTranslationTyped>['tt']
) {
  if (tokensRequired > tokensAvailable) {
    const needed = tokensRequired - tokensAvailable
    return needed === 1
      ? tt.pages.student.assignment.dialogs.enroll_dialog_body.enroll_outcome_purchase_needed_one(
          { count: needed }
        )
      : tt.pages.student.assignment.dialogs.enroll_dialog_body.enroll_outcome_purchase_needed_other(
          { count: needed }
        )
  }
  return tt.pages.student.assignment.dialogs.enroll_dialog_body.enroll_outcome_purchase_not_needed()
}

const EnrollOrPurchaseButton = function ({
  onEnroll,
  onPurchase,
  slideDeckPrice,
  requiresPurchase,
  section,
}: {
  onEnroll: () => void
  onPurchase: () => void
  slideDeckPrice: number
  requiresPurchase: boolean
  section: Section
}) {
  const { tt } = useTranslationTyped()
  const buttonText = requiresPurchase
    ? tt.student_assignment.purchase_tokens()
    : slideDeckPrice === 1
      ? tt.pages.student.assignment.dialogs.enroll_dialog_body.enroll_with_tokens_one(
          { count: slideDeckPrice }
        )
      : tt.pages.student.assignment.dialogs.enroll_dialog_body.enroll_with_tokens_other(
          { count: slideDeckPrice }
        )
  const onClick = requiresPurchase ? onPurchase : onEnroll

  const sectionStartDate = section.data.sectionStartDate
  const isSectionNotStarted = sectionStartDate && sectionStartDate > new Date()

  const button = (
    <button
      data-testid="enroll-with-tokens-button"
      className={classNames(
        'mb-[12px] rounded-2xl bg-core-primary px-6 py-4 text-label-large text-core-on-primary',
        requiresPurchase ? '' : 'w-full',
        isSectionNotStarted
          ? 'cursor-not-allowed opacity-50'
          : 'bg-core-primary text-core-on-primary'
      )}
      onClick={isSectionNotStarted ? undefined : onClick}
      disabled={isSectionNotStarted}
    >
      {buttonText}
    </button>
  )

  if (isSectionNotStarted) {
    return (
      <BreakoutTooltip
        content={tt.invitation.verify_invitation.section_not_accessible_yet()}
      >
        {button}
      </BreakoutTooltip>
    )
  }

  return button
}
